import React, { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { FlexibilityOption } from "@/lib/dateFlexibility";
import { loadPendingCheckout, savePendingCheckout } from "@/lib/pendingCheckout";

export interface QuoteItem {
  id: string;        // UUID from database
  itemId: string;    // Item code like "WASH1"
  name: string;
  unitType: string; // per_kg, per_metre, per_litre, per_item
  quantity: number;
}

export interface QuoteData {
  pickupCountry: string;
  pickupCountryId: string;
  pickupCity: string;
  pickupCityId: string;
  deliveryCountry: string;
  deliveryCountryId: string;
  deliveryCity: string;
  deliveryCityId: string;
  items: QuoteItem[];
  preferredDate: string;
  flexibility: FlexibilityOption;
  /** Computed from preferredDate - flexibility */
  earliestDate: string;
  /** Computed from preferredDate + flexibility */
  latestDate: string;
}

export interface CourierOption {
  id: string;
  name: string;
  route: string;
  pricePerKg: number;
  minWeight: number;
  deliveryTime: string;
  deliveryType: "Door" | "Depot" | "Both";
  verified: boolean;
  insurance: boolean;
  // ── Commercial truth (courier template currency, never converted) ──
  /** Courier pricing-template currency the customer is charged in. */
  currency?: string;
  finalPrice?: number;
  bookingFee?: number;
  payNow?: number;
  payToCourier?: number;
  // ── Accounting truth (MAD equivalents: ranking, tiering, settlement) ──
  finalPriceMAD?: number;
  bookingFeeMAD?: number;
  payNowMAD?: number;
  payToCourierMAD?: number;
}

export interface BookingInfo {
  fullName: string;
  email: string;
  whatsapp: string;
  pickupAddress: string;
  notes: string;
}

interface QuoteContextType {
  quoteData: QuoteData | null;
  setQuoteData: (data: QuoteData) => void;
  selectedCourier: CourierOption | null;
  setSelectedCourier: (courier: CourierOption) => void;
  bookingInfo: BookingInfo | null;
  setBookingInfo: (info: BookingInfo) => void;
  bookingRef: string;
  setBookingRef: (ref: string) => void;
}

const QuoteContext = createContext<QuoteContextType | undefined>(undefined);

export const QuoteProvider = ({ children }: { children: ReactNode }) => {
  // Website-only continuity: rehydrate from the persisted pending checkout so a
  // login/signup redirect or refresh never loses the quote + courier selection.
  const persisted = loadPendingCheckout();
  const [quoteData, setQuoteDataState] = useState<QuoteData | null>(
    (persisted?.quoteData as unknown as QuoteData | null) ?? null,
  );
  const [selectedCourier, setSelectedCourierState] = useState<CourierOption | null>(
    (persisted?.selectedCourier as unknown as CourierOption | null) ?? null,
  );
  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [bookingRef, setBookingRef] = useState("");

  const setQuoteData = useCallback((data: QuoteData) => {
    setQuoteDataState(data);
    savePendingCheckout({ quoteData: data as unknown as Record<string, unknown> });
  }, []);

  const setSelectedCourier = useCallback((courier: CourierOption) => {
    setSelectedCourierState(courier);
    savePendingCheckout({
      selectedCourier: courier as unknown as Record<string, unknown>,
      courierTripId: courier?.id ?? null,
      step: "courier_selected",
    });
  }, []);

  return (
    <QuoteContext.Provider
      value={{
        quoteData, setQuoteData,
        selectedCourier, setSelectedCourier,
        bookingInfo, setBookingInfo,
        bookingRef, setBookingRef,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
};

export const useQuote = () => {
  const ctx = useContext(QuoteContext);
  if (!ctx) throw new Error("useQuote must be used within QuoteProvider");
  return ctx;
};
